/**
 * Transaction Index Component
 *
 * Main container for the transaction list, integrating
 * filters, table, selection state, and load more functionality.
 *
 * @fileoverview Transaction index/navigator component
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useToast,
} from "@chakra-ui/react";
import { MdRefresh } from "react-icons/md";
import { useTransactions } from "../../hooks/useTransactions";
import { useAccounts } from "../../hooks/useAccounts";
import { useUser } from "../../Context/userContext";
import { useAuthContext } from "../../contexts/AuthContext";
import { useTransactionSync } from "../../hooks/useTransactionSync";
import { apiJson } from "../../lib/api";
import {
  TransactionFilters as TransactionFiltersType,
  TransactionSortColumn,
} from "../../types/transactions";
import { AccountSelector } from "../common/AccountSelector";
import { TablePagination } from "../common/TablePagination";
import { PageSizeSelect } from "../common/PageSizeSelect";
import TransactionFilters from "./TransactionFilters";
import TransactionTable from "./TransactionTable";
import TransactionSyncButtons from "./TransactionSyncButtons";

/**
 * Selection state
 */
interface SelectionState {
  selectedIds: Set<string>;
  selectAllMode: "none" | "displayed" | "all";
}

type AccountSelectionPhase = "idle" | "pending" | "loading";

const areSameAccountSelection = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const leftSet = new Set(left);
  return right.every((id) => leftSet.has(id));
};

/**
 * Transaction Index Component
 *
 * Orchestrates the transaction list view with:
 * - Filter controls
 * - Sortable/selectable table
 * - Selection state management
 * - Load more pagination
 * - Loading/error/empty states
 */
const TransactionIndex: React.FC = () => {
  const { plaidTokenStatus, selectedTenant } = useUser();
  const { session } = useAuthContext();
  const { accounts, isLoading: isLoadingAccounts, refresh: refreshAccounts } = useAccounts();
  const { syncIncremental, syncFull, isSyncing, syncType, error: syncError } = useTransactionSync();
  const toast = useToast();

  const [accountSelectionPhase, setAccountSelectionPhase] = useState<AccountSelectionPhase>("idle");

  // Delayed loading modal: show only if accounts loading exceeds 2 seconds
  const [showAccountsLoadingModal, setShowAccountsLoadingModal] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoadingAccounts) {
      timeoutRef.current = setTimeout(() => {
        setShowAccountsLoadingModal(true);
      }, 2000);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowAccountsLoadingModal(false);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoadingAccounts]);

  const {
    transactions,
    totalCount,
    page,
    pageSize,
    totalPages,
    isLoading,
    error,
    filters,
    sort,
    setPage,
    setPageSize,
    applyFilters,
    updateFilters,
    toggleSort,
    clearFilters,
    refresh,
    updateVendor,
  } = useTransactions();

  // Holds whichever row's inline-edit commit function is currently
  // registered (see TransactionRow's onEditingActiveChange) — pagination
  // flushes it before navigating away, so an in-progress edit is saved
  // instead of lost when the row unmounts on page change. Same mechanism
  // as TransactionsExplorer's Scenario Editor / /transactions tables — see
  // docs/plans/TRANSACTIONS_PAGINATION_PLAN.md, R2-A.
  const pendingRowCommitRef = useRef<(() => Promise<boolean>) | null>(null);
  const flushPendingRowEdit = useCallback(async (): Promise<boolean> => {
    if (!pendingRowCommitRef.current) return true;
    const ok = await pendingRowCommitRef.current();
    if (!ok) {
      toast({
        title: "Finish or cancel the current edit before changing page",
        status: "warning",
        duration: 3000,
      });
    }
    return ok;
  }, [toast]);

  // Selection state
  const [selection, setSelection] = useState<SelectionState>({
    selectedIds: new Set(),
    selectAllMode: "none",
  });

  // Use filters.accountIds directly instead of separate state
  const selectedAccountIds = filters.accountIds ?? [];

  const handleAccountIdsChange = useCallback(
    (ids: string[]) => {
      if (!areSameAccountSelection(ids, selectedAccountIds)) {
        setAccountSelectionPhase("pending");
      }
      // Use functional update to avoid stale closure
      updateFilters((prevFilters) => ({
        ...prevFilters,
        accountIds: ids,
        accountId: undefined,
      }));
      setSelection({ selectedIds: new Set(), selectAllMode: "none" });
    },
    [selectedAccountIds, updateFilters],
  );

  /**
   * Get the tokenId for the selected account (for targeted sync)
   * If no account selected (All Accounts), returns undefined to sync all banks
   */
  const selectedTokenId = useMemo(() => {
    if (selectedAccountIds.length !== 1) return undefined;
    const selectedAccount = accounts.find((a) => a.id === selectedAccountIds[0]);
    return selectedAccount?.plaidTokenId ?? undefined;
  }, [selectedAccountIds, accounts]);

  /**
   * Filter dropdown options, fetched once per tenant from the tenant's
   * ENTIRE transaction history — not derived from `transactions` (now just
   * the current page, up to pageSize rows). Deriving them from the loaded
   * page would mean the dropdowns only ever offered whatever categories
   * happened to appear on that one page.
   */
  const [filterOptions, setFilterOptions] = useState<{ pfcPrimary: string[]; pfcDetailed: string[] }>({
    pfcPrimary: [],
    pfcDetailed: [],
  });
  useEffect(() => {
    if (!session?.access_token || !selectedTenant?.id) return;
    apiJson<{ pfcPrimary: string[]; pfcDetailed: string[] }>(
      "/api/plaid-transactions/filter-options",
      { headers: { "x-tenant-id": selectedTenant.id } },
      session.access_token,
    )
      .then(setFilterOptions)
      .catch((err) => console.error("Failed to load filter options:", err));
  }, [session?.access_token, selectedTenant?.id]);

  /**
   * Handle individual transaction selection
   */
  const handleSelectTransaction = useCallback((id: string) => {
    setSelection((prev) => {
      const newSelectedIds = new Set(prev.selectedIds);

      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }

      // If we were in "all" mode and deselected something, revert to "displayed"
      const newMode =
        prev.selectAllMode === "all" && newSelectedIds.size < prev.selectedIds.size
          ? "displayed"
          : prev.selectAllMode;

      return {
        selectedIds: newSelectedIds,
        selectAllMode: newSelectedIds.size === 0 ? "none" : newMode,
      };
    });
  }, []);

  /**
   * Handle select all displayed transactions
   */
  const handleSelectAllDisplayed = useCallback(() => {
    setSelection((prev) => {
      const allDisplayedIds = new Set(transactions.map((t) => t.id));
      const allCurrentlySelected = transactions.every((t) => prev.selectedIds.has(t.id));

      if (allCurrentlySelected && prev.selectedIds.size > 0) {
        // Deselect all
        return {
          selectedIds: new Set(),
          selectAllMode: "none",
        };
      } else {
        // Select all displayed
        return {
          selectedIds: allDisplayedIds,
          selectAllMode: "displayed",
        };
      }
    });
  }, [transactions]);

  /**
   * Handle select all transactions (beyond displayed)
   */
  const handleSelectAll = useCallback(() => {
    setSelection({
      selectedIds: new Set(transactions.map((t) => t.id)),
      selectAllMode: "all",
    });
  }, [transactions]);

  /**
   * Handle filter application
   */
  const handleApplyFilters = useCallback(
    (newFilters: TransactionFiltersType) => {
      applyFilters(newFilters);
      // Clear selection when filters change
      setSelection({ selectedIds: new Set(), selectAllMode: "none" });
    },
    [applyFilters],
  );

  /**
   * Handle clear filters
   */
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSelection({ selectedIds: new Set(), selectAllMode: "none" });
  }, [clearFilters]);

  /**
   * Handle sort toggle
   */
  const handleToggleSort = useCallback(
    (column: TransactionSortColumn) => {
      toggleSort(column);
    },
    [toggleSort],
  );

  const handleSaveVendor = useCallback(
    async (id: string, vendor: string) => {
      await updateVendor(id, vendor);
    },
    [updateVendor],
  );

  /**
   * Handle refresh - refreshes both transactions and accounts
   * (accounts may have been created during sync)
   */
  const handleRefresh = useCallback(async () => {
    await Promise.all([refresh(), refreshAccounts()]);
    setSelection({ selectedIds: new Set(), selectAllMode: "none" });
  }, [refresh, refreshAccounts]);

  useEffect(() => {
    if (accountSelectionPhase === "pending" && isLoading) {
      setAccountSelectionPhase("loading");
    } else if (accountSelectionPhase === "loading" && !isLoading) {
      setAccountSelectionPhase("idle");
    }
  }, [accountSelectionPhase, isLoading]);

  // Error state - only if no data
  if (error && transactions.length === 0) {
    return (
      <Center h="300px">
        <VStack spacing={4}>
          <Alert
            status="error"
            bg="red.900"
            borderColor="red.500"
            borderWidth="1px"
            borderRadius="md"
            maxW="400px"
          >
            <AlertIcon color="red.300" />
            <Text color="red.200" fontSize="sm">
              {error}
            </Text>
          </Alert>
          <Button
            leftIcon={<MdRefresh />}
            size="sm"
            variant="outline"
            color="accent.primary"
            borderColor="xacto.electricCyan"
            onClick={handleRefresh}
            _hover={{ bg: "xacto.mutedNavy" }}
          >
            Retry
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={4} align="stretch" h="100%">
      {/* Sticky upper panel: header + filters */}
      <Box position="sticky" top="120px" zIndex={14} bg="bg.primary" pb={2}>
        {/* Header */}
        <HStack justify="space-between" wrap="wrap" gap={2} mb={4}>
          <HStack spacing={3}>
            <Text color="text.primary" fontWeight="semibold" fontSize="lg">
              Transactions
            </Text>
            <HStack spacing={2}>
              <AccountSelector
                values={selectedAccountIds}
                onChangeMulti={handleAccountIdsChange}
                multiSelect
                compact
                size="sm"
              />
              {accountSelectionPhase !== "idle" && <Spinner size="sm" color="accent.primary" />}
            </HStack>
            <Badge colorScheme="cyan" variant="subtle" fontSize="sm">
              {isLoading && totalCount === 0 ? "..." : totalCount.toLocaleString()} total
            </Badge>
            {selection.selectedIds.size > 0 && (
              <Badge colorScheme="green" variant="subtle" fontSize="sm">
                {selection.selectAllMode === "all"
                  ? `${totalCount.toLocaleString()} selected`
                  : `${selection.selectedIds.size} selected`}
              </Badge>
            )}
          </HStack>

          <HStack spacing={2}>
            {/* Sync Buttons - syncs selected bank or all banks if no account selected */}
            <TransactionSyncButtons
              plaidTokenStatus={plaidTokenStatus?.status}
              onSyncComplete={handleRefresh}
              tokenId={selectedTokenId}
              accountIds={selectedAccountIds}
              syncIncremental={syncIncremental}
              syncFull={syncFull}
              isSyncing={isSyncing}
              syncType={syncType}
              syncError={syncError}
            />

            {/* Refresh Button */}
            <Button
              leftIcon={<MdRefresh />}
              size="sm"
              variant="ghost"
              color="text.secondary"
              onClick={handleRefresh}
              isLoading={isLoading}
              _hover={{ bg: "xacto.mutedNavy" }}
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        {/* Filters */}
        <TransactionFilters
          filters={filters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          pfcPrimaryOptions={filterOptions.pfcPrimary}
          pfcDetailedOptions={filterOptions.pfcDetailed}
        />
      </Box>

      {/* Table / Loading State */}
      <Box flex="1" overflow="hidden">
        {isLoading && transactions.length === 0 ? (
          <Center h="300px">
            <VStack spacing={4}>
              <Spinner size="lg" color="accent.primary" thickness="3px" />
              <Text color="text.secondary" fontSize="sm">
                Loading transactions...
              </Text>
            </VStack>
          </Center>
        ) : (
          <TransactionTable
            transactions={transactions}
            selectedIds={selection.selectedIds}
            selectAllMode={selection.selectAllMode}
            totalCount={totalCount}
            sort={sort}
            onToggleSort={handleToggleSort}
            onSelectTransaction={handleSelectTransaction}
            onSelectAllDisplayed={handleSelectAllDisplayed}
            onSelectAll={handleSelectAll}
            onSaveVendor={handleSaveVendor}
            disableCrossPageSelectAll
            onEditingActiveChange={(commit) => {
              pendingRowCommitRef.current = commit;
            }}
          />
        )}
      </Box>

      {/* Pagination */}
      {transactions.length > 0 && (
        <HStack justify="space-between" py={3} wrap="wrap" rowGap={2}>
          <HStack spacing={4}>
            <Text color="text.muted" fontSize="sm">
              {`Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount.toLocaleString()}`}
            </Text>
            <PageSizeSelect pageSize={pageSize} onChange={setPageSize} isDisabled={isLoading} />
          </HStack>
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onBeforePageChange={flushPendingRowEdit}
            isLoading={isLoading}
          />
        </HStack>
      )}

      {/* Loading modal: accounts loading or sync in progress */}
      <Modal
        isOpen={showAccountsLoadingModal || isSyncing}
        onClose={() => {}}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px" maxW="sm">
          <ModalBody py={10}>
            <VStack spacing={4}>
              <Spinner color="accent.primary" size="xl" thickness="4px" />
              <Text color="text.secondary" fontSize="md">
                {isSyncing
                  ? syncType === "full"
                    ? "Running full sync..."
                    : "Syncing transactions..."
                  : "Loading accounts..."}
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default TransactionIndex;
